/**
 * KpiStrip — Zona 2 do layout de 3 zonas: strip de indicadores + card
 * opcional de "próxima ação recomendada".
 *
 * Onda 1 (fidelidade Bloco D): emite as classes canônicas `.kpi-strip-v2`
 * de components.css (byte-a-byte do mockup Fase 1). Valores NEUTROS (decisão
 * 2026: idêntico ao mockup — cor de estado vai pro delta/meta, não pro
 * número). Grid se adapta: com `nextAction` reserva a coluna destacada
 * (220-280px); sem ela, colunas iguais sem vão.
 *
 * Props (API preservada):
 * - `items`: [{ label, value, sub?, tone?, href?, onClick?, key?, title? }]
 *   (`title` vira tooltip nativo do card — ex.: decomposição do buffer)
 * - `orientation`: "horizontal" (default) | "vertical"
 * - `nextAction`: { label, title, cta?, onClick } — card destacado ao fim
 * - `standalone` (default true): card com raio/borda completos. `false`
 *   conecta com `.eh` logo acima (sem borda superior).
 */
import React from "react";

import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";

function KpiStrip({
  items = [],
  orientation = "horizontal",
  nextAction,
  standalone = true,
}) {
  // Auditoria mobile 2026-09-24: abaixo de 768px a strip horizontal vira
  // uma faixa de UMA linha rolável (responsive-additions.css). Uma coluna
  // explícita inline venceria o `grid-auto-flow: column` de lá, então no
  // celular não se aplica gridTemplateColumns. Vertical segue igual.
  const isMobile = useMediaQuery(mq.mobile);

  if (!items.length && !nextAction) return null;

  // Grid: canônico reserva coluna p/ next-action. Sem ela, colunas iguais.
  // Vertical (usado em side-panels): empilha em 1 coluna.
  let gridTemplateColumns;
  if (orientation === "vertical") gridTemplateColumns = "1fr";
  else if (!nextAction && !isMobile) gridTemplateColumns = "repeat(auto-fit, minmax(140px, 1fr))";

  const style = { marginBottom: 16 };
  if (gridTemplateColumns) style.gridTemplateColumns = gridTemplateColumns;

  return (
    <div
      className={`kpi-strip-v2${standalone ? " standalone" : ""}`}
      style={style}
    >
      {items.map((item, idx) => {
        const clickable = Boolean(item.href || item.onClick);
        const handleKey = clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                item.onClick?.();
              }
            }
          : undefined;
        return (
          <div
            key={item.key || `${item.label}-${idx}`}
            className="kpi-v2"
            onClick={item.onClick}
            onKeyDown={handleKey}
            role={clickable ? "button" : "status"}
            tabIndex={clickable ? 0 : undefined}
            style={clickable ? { cursor: "pointer" } : undefined}
            aria-label={`${item.label}: ${item.value}`}
            title={item.title || undefined}
          >
            <div className="lbl">{item.label}</div>
            <div className="val">{item.value}</div>
            {item.sub && <div className="meta">{item.sub}</div>}
          </div>
        );
      })}

      {nextAction && (
        <div className="next-action">
          <div className="lbl">{nextAction.label || "Próxima ação"}</div>
          <div className="title">{nextAction.title}</div>
          {nextAction.cta && (
            <button
              type="button"
              className="action-link"
              onClick={nextAction.onClick}
            >
              {nextAction.cta}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default KpiStrip;
